// frontend/src/pages/PublishItemPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const formatPriceInput = (value) => {
  if (!value) return '';
  const numericValue = parseInt(value, 10);
  if (isNaN(numericValue)) return '';
  return new Intl.NumberFormat('es-PY').format(numericValue);
};

const parsePriceInput = (formattedValue) => formattedValue.replace(/[^0-9]/g, '');

function PublishItemPage() {
  const [formData, setFormData] = useState({
    titulo: '',
    descripcion: '',
    precio: '',
    tipo_operacion: 'Venta',
    estado: 'Usado',
    facultad_id: '',
  });
  const [imagen, setImagen] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [universidades, setUniversidades] = useState([]);
  const [selectedUniversidadId, setSelectedUniversidadId] = useState('');
  const [facultades, setFacultades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const esServicio = formData.tipo_operacion === 'Servicio';

  useEffect(() => {
    api.get('/general/universidades')
      .then(r => setUniversidades(r.data))
      .catch(() => setError('No se pudieron cargar las universidades.'));
  }, []);

  useEffect(() => {
    if (selectedUniversidadId) {
      const universidad = universidades.find(u => u.id === parseInt(selectedUniversidadId));
      setFacultades(universidad ? universidad.facultades : []);
      setFormData(prev => ({ ...prev, facultad_id: '' }));
    } else {
      setFacultades([]);
    }
  }, [selectedUniversidadId, universidades]);

  // Si cambia a Servicio, resetear estado a 'N/A'
  useEffect(() => {
    if (esServicio) {
      setFormData(prev => ({ ...prev, estado: 'N/A' }));
    } else if (formData.estado === 'N/A') {
      setFormData(prev => ({ ...prev, estado: 'Usado' }));
    }
  }, [formData.tipo_operacion]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'precio') {
      const numericValue = parsePriceInput(value);
      if (numericValue.length <= 12) setFormData(prev => ({ ...prev, precio: numericValue }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImagen(file);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    } else {
      setImagen(null);
      setImagePreview(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!formData.titulo || !formData.precio) {
      setError('El titulo y el precio son obligatorios.');
      setLoading(false);
      return;
    }

    try {
      let imagen_filename = null;

      if (imagen) {
        const presignedResponse = await api.get('/upload/presigned-url', {
          params: { content_type: imagen.type }
        });
        const { presigned_url, filename } = presignedResponse.data;
        await fetch(presigned_url, {
          method: 'PUT',
          headers: { 'Content-Type': imagen.type },
          body: imagen,
        });
        imagen_filename = filename;
      }

      const itemData = {
        ...formData,
        precio: parseFloat(formData.precio) || 0,
        facultad_id: formData.facultad_id ? parseInt(formData.facultad_id) : null,
      };

      const response = await api.post('/items/', {
        item_data: itemData,
        imagen_filename
      });

      navigate(`/items/${response.data.id}`);

    } catch (err) {
      let errorMsg = 'Ocurrio un error al publicar.';
      if (err.response?.data?.detail) {
        if (typeof err.response.data.detail === 'string') {
          errorMsg = err.response.data.detail;
        }
      }
      setError(errorMsg);
      setLoading(false);
    }
  };

  return (
    <div className="container mt-4" style={{ maxWidth: '680px' }}>
      <h2 className="mb-4">Publicar</h2>
      {error && <div className="alert alert-danger">{error}</div>}

      <form onSubmit={handleSubmit}>
        <div className="mb-3">
          <label className="form-label fw-semibold">Tipo de Operacion</label>
          <div className="d-flex gap-2">
            {['Venta', 'Prestamo', 'Servicio'].map(tipo => (
              <button
                key={tipo}
                type="button"
                className={`btn flex-fill ${formData.tipo_operacion === tipo ? 'btn-primary' : 'btn-outline-secondary'}`}
                onClick={() => setFormData(prev => ({ ...prev, tipo_operacion: tipo }))}
              >
                {tipo === 'Venta' && <i className="bi bi-tag me-1"></i>}
                {tipo === 'Prestamo' && <i className="bi bi-arrow-left-right me-1"></i>}
                {tipo === 'Servicio' && <i className="bi bi-tools me-1"></i>}
                {tipo}
              </button>
            ))}
          </div>
          {esServicio && (
            <small className="text-muted mt-1 d-block">
              Ofrece tus habilidades: maquetas, diseno, programacion, traducciones, etc.
            </small>
          )}
        </div>

        <div className="mb-3">
          <label className="form-label fw-semibold">
            {esServicio ? 'Nombre del Servicio' : 'Titulo'}
          </label>
          <input
            type="text"
            className="form-control"
            name="titulo"
            value={formData.titulo}
            onChange={handleChange}
            placeholder={esServicio ? 'Ej: Maquetas para arquitectura, Diseno de logos...' : 'Titulo del producto'}
            required
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-semibold">Descripcion</label>
          <textarea
            className="form-control"
            name="descripcion"
            rows="3"
            value={formData.descripcion}
            onChange={handleChange}
            placeholder={esServicio
              ? 'Describe que ofreces, tiempos de entrega, materiales incluidos...'
              : 'Describe el producto, su condicion, que incluye...'}
          />
        </div>

        <div className="row mb-3">
          <div className={esServicio ? 'col-12' : 'col-md-6'}>
            <label className="form-label fw-semibold">
              {esServicio ? 'Precio del Servicio (Gs.)' : 'Precio (Gs.)'}
            </label>
            <input
              type="text"
              inputMode="numeric"
              className="form-control"
              name="precio"
              value={formatPriceInput(formData.precio)}
              onChange={handleChange}
              placeholder="0"
              required
            />
            {esServicio && (
              <small className="text-muted">Podes poner precio base o estimado.</small>
            )}
          </div>

          {/* Estado solo aplica a productos físicos */}
          {!esServicio && (
            <div className="col-md-6">
              <label className="form-label fw-semibold">Estado del Producto</label>
              <select className="form-select" name="estado" value={formData.estado} onChange={handleChange} required>
                <option value="Nuevo">Nuevo</option>
                <option value="Usado">Usado</option>
              </select>
            </div>
          )}
        </div>

        <div className="row mb-3">
          <div className="col-md-6">
            <label className="form-label fw-semibold">Universidad (Opcional)</label>
            <select
              className="form-select"
              value={selectedUniversidadId}
              onChange={e => setSelectedUniversidadId(e.target.value)}
            >
              <option value="">-- Selecciona Universidad --</option>
              {universidades.map(uni => <option key={uni.id} value={uni.id}>{uni.nombre}</option>)}
            </select>
          </div>
          <div className="col-md-6">
            <label className="form-label fw-semibold">Facultad (Opcional)</label>
            <select
              className="form-select"
              name="facultad_id"
              value={formData.facultad_id}
              onChange={handleChange}
              disabled={!selectedUniversidadId}
            >
              <option value="">-- Selecciona Facultad --</option>
              {facultades.map(fac => <option key={fac.id} value={fac.id}>{fac.nombre}</option>)}
            </select>
          </div>
        </div>

        <div className="mb-4">
          <label className="form-label fw-semibold">
            {esServicio ? 'Imagen de referencia (Opcional)' : 'Imagen (Opcional)'}
          </label>
          <input type="file" className="form-control" accept="image/*" onChange={handleImageChange} />
          {imagePreview && (
            <img src={imagePreview} alt="Vista previa" className="img-thumbnail mt-2" style={{ maxHeight: '200px' }} />
          )}
        </div>

        <button type="submit" className="btn btn-primary w-100" disabled={loading}>
          {loading ? 'Publicando...' : (esServicio ? 'Publicar Servicio' : 'Publicar Producto')}
        </button>
      </form>
    </div>
  );
}

export default PublishItemPage;
